﻿import {runViewTransition} from "@2d-front-monorepo/core";
import {Children, FC, JSX, memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Icon from "../Icon";
import {Handler} from "./Handler";
import {WizardContext} from "./WizardContext";
import {WizardProps} from "./WizardProps";
import {WizardStepProps} from "./WizardStep";

interface WizardStepTabProps {
	label: string,
	activeStep: number,
	gotoStep: (stepIndex: number) => void,
	index: number
}

function WizardStepTab(props: WizardStepTabProps): JSX.Element {
	const {label, activeStep, gotoStep, index} = props;
	const isDisabled = index > activeStep;
	const isCompleted = index < activeStep;
	const isCurrent = activeStep === index;

	return <div className={"wizard-steps__step" + (isCompleted ? " wizard-steps__step--completed" : "")}>
		{index > 0 ? <hr/> : undefined}
		<button
			type="button"
			role="tab"
			onClick={isCompleted ? e => {
				e.preventDefault();
				gotoStep(index);
			} : e => e.preventDefault()}
			tabIndex={isCompleted ? 0 : -1}
			aria-disabled={isDisabled}
			aria-selected={isCurrent}
			// The tab is `step_N` and the panel it drives is `panel_N`. The two were crossed: the
			// tab had no id at all, so the panel's aria-labelledby resolved to nothing, and this
			// aria-controls named the tab's own id rather than the panel's.
			id={`step_${index}`}
			aria-controls={`panel_${index}`}>
			<span className="wizard-steps__step-number">{isCompleted ? <Icon icon="wizard-step"/> : index + 1}</span>
			<span className="wizard-steps__step-label">{label}</span>
		</button>
	</div>;
}

const MemoizedWizardStepTab: FC<WizardStepTabProps> = memo(WizardStepTab);
const STEP_BACK_STYLE = "wizard-steps--back";

function Wizard(props: WizardProps): JSX.Element {
	const {header, footer, onStepChange, startIndex, startAt} = props;

	const allSteps = Children.toArray(props.children) as ReactElement<WizardStepProps>[];
	// Positions of the on-screen steps within the full child list — the steps themselves, plus
	// the identity behind each position.
	const visible = allSteps.flatMap((step, index) => step.props.hidden ? [] : [index]);
	const steps = visible.map(index => allSteps[index]);
	const stepCount = steps.length;

	// `startAt` names the step to open on instead of numbering it: a position moves with every
	// hidden sibling, a name does not. An unknown or hidden name falls back to `startIndex`.
	const [activeStep, setActiveStep] = useState(() => {
		const named = startAt == null ? -1 : steps.findIndex(step => step.props.name === startAt);
		return named >= 0 ? named : startIndex ?? 0;
	});
	const [isLoading, setIsLoading] = useState(false);
	const hasNextStep = useRef(true);
	const hasPreviousStep = useRef(false);
	const wizardRef = useRef<HTMLDivElement>(null);
	const isTransitioning = useRef(false);
	const isAdvancing = useRef(false);
	const nextStepHandler = useRef<Handler>(() => {
	});

	// `activeStep` is a POSITION among the visible steps, and navigation deliberately reads it
	// that way: "next" means the slot after mine in the list as it stands AFTER onStepChange has
	// run, so a step that just became required is stepped into rather than skipped (and one that
	// just became irrelevant is skipped rather than shown). Hosts rely on both directions.
	//
	// What a bare position leaves broken is the PASSIVE case — the visible set changing with no
	// navigation behind it, which is the norm once a step's `hidden` reads state a step body or
	// an async load owns. The position then quietly belongs to a different step: the user is
	// moved without touching anything, and the handler registered by the step they were on is
	// still the one `nextStep()` would run. So remember which step the position last resolved
	// to, and when that moves with the position itself untouched, follow the step.
	const previousActive = useRef(activeStep);
	const anchor = useRef(-1);
	// The visible list as it stands THIS render, for changeStepFast to name the step it moves to:
	// the callback is memoized, `steps` is rebuilt every render.
	const stepsRef = useRef(steps);
	stepsRef.current = steps;

	let clampedActiveStep = Math.min(activeStep, stepCount - 1);
	const anchored = visible.indexOf(anchor.current);
	// anchored < 0: the step really is gone (hidden under the user, or never anchored) — take
	// whatever now occupies the position, which is the next one along.
	if (previousActive.current === activeStep && anchored >= 0) {
		clampedActiveStep = anchored;
	}

	const currentIndex = visible[clampedActiveStep] ?? -1;
	previousActive.current = activeStep;
	anchor.current = currentIndex;

	hasNextStep.current = clampedActiveStep < stepCount - 1;
	hasPreviousStep.current = clampedActiveStep > 0;

	const changeStepFast = useCallback(function (newStep: number) {
		nextStepHandler.current = undefined;
		setActiveStep(newStep);
		onStepChange?.(stepsRef.current[newStep]?.props.name);
	}, [stepCount, onStepChange]);

	const changeStep = useCallback(async function (oldStep: number, newStep: number): Promise<void> {
		// Ignore step changes triggered while a transition is still animating. Without this, a rapid
		// double-click (or a programmatic change racing the user) starts a second view transition that
		// interrupts the first, causing jank or skipped steps. The buttons themselves stay enabled during
		// the animation, so the guard lives here rather than relying on disabled state.
		if (isTransitioning.current) return;
		if (newStep < 0 || newStep >= stepCount || newStep === oldStep) return;

		const wizard = wizardRef.current;
		if (!wizard) {
			changeStepFast(newStep);
			return;
		}

		// The direction class drives the `.wizard-steps--back::view-transition-*` selectors in wizard.less.
		// Those pseudo-elements are rooted at whatever element owns the transition: today element-scoped
		// startViewTransition isn't supported, so runViewTransition falls back to document.startViewTransition
		// and the tree lives on :root — hence the class must go on documentElement for the back animation to
		// match. Once element-scoped transitions ship and the transition runs on `wizard`, move this toggle
		// onto `wizard` (it already carries `wizard-steps`, so the selectors keep matching).
		const root = document.documentElement;
		isTransitioning.current = true;
		try {
			await runViewTransition(
				wizard,
				() => changeStepFast(newStep),
				() => root.classList.toggle(STEP_BACK_STYLE, newStep < oldStep),
				() => root.classList.remove(STEP_BACK_STYLE)
			);
		} finally {
			isTransitioning.current = false;
		}

	}, [changeStepFast, stepCount]);

	const gotoNextStep = useCallback((): Promise<void> => {
		return hasNextStep.current ? changeStep(clampedActiveStep, clampedActiveStep + 1) : Promise.resolve();
	}, [clampedActiveStep, changeStep]);

	const gotoPreviousStep = useCallback((): Promise<void> => {
		return hasPreviousStep.current ? changeStep(clampedActiveStep, clampedActiveStep - 1) : Promise.resolve();
	}, [clampedActiveStep, changeStep]);

	const gotoStep = useCallback(
		(stepIndex: number): Promise<void> => changeStep(clampedActiveStep, stepIndex),
		[clampedActiveStep, changeStep]
	);

	const handleStep = useRef((handler: Handler) => {
		nextStepHandler.current = handler;
	});

	const doNextStep = useCallback(async () => {
		// Don't start advancing while another step change is still in flight — either this handler, or a
		// back/forward transition started elsewhere. nextStep() now resolves only after the view transition
		// finishes and not every step disables its own controls (some use plain buttons), so without this
		// guard a second invocation would re-run the handler, re-submitting data or creating duplicates.
		// A ref is used rather than `isLoading` so the guard is race-free against clicks within one tick.
		if (isAdvancing.current || isTransitioning.current) return;

		isAdvancing.current = true;
		try {
			if (hasNextStep.current && nextStepHandler.current) {
				// isLoading stays scoped to the handler promise (per WizardValues docs); the finally keeps
				// that contract while guaranteeing it resets even if the handler throws.
				setIsLoading(true);
				try {
					await nextStepHandler.current();
				} finally {
					setIsLoading(false);
				}
			}
			await gotoNextStep();
		} finally {
			isAdvancing.current = false;
		}
	}, [gotoNextStep]);

	const wizardValue = useMemo(() => ({
		nextStep: doNextStep,
		previousStep: gotoPreviousStep,
		handleStep: handleStep.current,
		isLoading,
		activeStep: clampedActiveStep,
		stepCount,
		isFirstStep: !hasPreviousStep.current,
		isLastStep: !hasNextStep.current,
		gotoStep
	}), [doNextStep, gotoPreviousStep, isLoading, clampedActiveStep, stepCount, gotoStep]);

	const currentStep = steps[clampedActiveStep];
	const stepsContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const container = stepsContainerRef.current;
		if (!container) return;

		const stepElement = container.querySelector<HTMLElement>(`[aria-selected="true"]`)?.closest<HTMLElement>(".wizard-steps__step");
		if (!stepElement) return;

		const paddingLeft = parseFloat(getComputedStyle(stepElement).paddingInlineStart) || 0;
		container.scrollTo({left: stepElement.offsetLeft - paddingLeft, behavior: "smooth"});
	}, [clampedActiveStep]);

	return <WizardContext value={wizardValue}>
		<div className="wizard-steps" role="dialog" aria-labelledby="wizard-title" ref={wizardRef}>
			{header}

			<div className="wizard-steps__buttons__container" ref={stepsContainerRef}>
				<div className="wizard-steps__buttons" role="tablist">
					{steps.map((step, i) =>
						<MemoizedWizardStepTab index={i}
						                       label={step.props.title}
						                       activeStep={clampedActiveStep}
						                       gotoStep={gotoStep}
						                       key={i}/>
					)}
				</div>
			</div>

			{/* currentStep can be undefined when every step is hidden.
			    Keyed by the step's own index in the child list, not by its position: without a key
			    React reconciled the next step's tree into the previous step's DOM, so an
			    uncontrolled input (or anything else holding state in a like-shaped subtree) carried
			    its value across the step change. Keyed by IDENTITY so a step merely sliding to
			    another position — the whole point of a `hidden` toggle — doesn't remount it. */}
			{currentStep != null &&
                <div key={currentIndex} role="tabpanel" aria-labelledby={"step_" + clampedActiveStep} id={"panel_" + clampedActiveStep}>
					{currentStep.props.children(currentStep.props)}
                </div>}

			{footer}
		</div>
	</WizardContext>;
}

const MemoizedWizard: FC<WizardProps> = memo(Wizard);
export default MemoizedWizard;
